import React, { useState, useEffect } from 'react';
import { 
  Users, Loader2, X, Phone, Mail, Calendar, Send,
  Clock, CheckCircle, XCircle, User, MessageSquare
} from 'lucide-react';
import { studentLeadershipAPI } from '../../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS_CONFIG = {
  pending: { label: 'Jarayonda', color: '#f59e0b', bg: '#fef3c7', icon: Clock },
  accepted: { label: 'Qabul qilindi', color: '#22c55e', bg: '#dcfce7', icon: CheckCircle },
  rejected: { label: 'Rad qilindi', color: '#ef4444', bg: '#fee2e2', icon: XCircle },
};

const StudentLeadership = () => {
  const [leaders, setLeaders] = useState([]);
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const [selectedAppeal, setSelectedAppeal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
  });

  // Rasm URL ni olish
  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${API_URL}${photo}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadersRes, appealsRes] = await Promise.all([
        studentLeadershipAPI.getAll(),
        studentLeadershipAPI.getAppeals()
      ]);
      setLeaders(leadersRes.data.leaders || []);
      setAppeals(appealsRes.data.appeals || []);
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await studentLeadershipAPI.createAppeal({
        leadership: selectedLeader.id,
        ...formData
      });
      setShowModal(false);
      setFormData({ subject: '', message: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Xatolik yuz berdi');
    } finally {
      setSubmitting(false);
    }
  };

  const openAppealModal = (leader) => {
    setSelectedLeader(leader);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Users size={28} /> Rahbariyat</h1>
          <p>Rahbariyatga murojaat qiling</p>
        </div>
      </header>

      {/* Rahbariyat ro'yxati */}
      <div className="leadership-section">
        <h3>Rahbariyat a'zolari</h3>
        {leaders.length === 0 ? (
          <div className="empty-state">
            <Users size={48} />
            <p>Rahbariyat topilmadi</p>
          </div>
        ) : (
          <div className="leaders-modern-grid">
            {leaders.map(leader => (
              <div key={leader.id} className="leader-modern-card">
                <div className="leader-modern-header">
                  <div className="leader-modern-avatar">
                    {leader.photo ? (
                      <img src={getPhotoUrl(leader.photo)} alt={leader.full_name} />
                    ) : (
                      <User size={48} />
                    )}
                  </div>
                  <div className="leader-modern-info">
                    <h4>{leader.full_name}</h4>
                    <span className="leader-position-badge">{leader.position}</span>
                  </div>
                </div>
                
                <div className="leader-modern-details">
                  {leader.reception_days && (
                    <div className="detail-row">
                      <Calendar size={16} />
                      <span>{leader.reception_days}</span>
                    </div>
                  )}
                  {leader.phone && (
                    <div className="detail-row">
                      <Phone size={16} />
                      <a href={`tel:${leader.phone}`}>{leader.phone}</a>
                    </div>
                  )}
                  {leader.email && (
                    <div className="detail-row">
                      <Mail size={16} />
                      <a href={`mailto:${leader.email}`}>{leader.email}</a>
                    </div>
                  )}
                </div>
                
                <button 
                  className="leader-appeal-btn"
                  onClick={() => openAppealModal(leader)}
                >
                  <MessageSquare size={18} />
                  Taklif va murojaatlar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mening murojaatlarim */}
      {appeals.length > 0 && (
        <div className="leadership-section">
          <h3>Mening murojaatlarim</h3>
          <div className="my-appeals-list">
            {appeals.map(appeal => {
              const statusConfig = STATUS_CONFIG[appeal.status];
              const StatusIcon = statusConfig.icon;
              return (
                <div 
                  key={appeal.id} 
                  className="my-appeal-card"
                  onClick={() => setSelectedAppeal(appeal)}
                >
                  <div className="appeal-top">
                    <div className="appeal-badge" style={{ background: statusConfig.bg, color: statusConfig.color }}>
                      <StatusIcon size={14} />
                      {statusConfig.label}
                    </div>
                    <span className="appeal-date">
                      {new Date(appeal.created_at).toLocaleDateString('uz-UZ')}
                    </span>
                  </div>
                  <h4>{appeal.subject}</h4>
                  <p className="appeal-to">
                    <Users size={14} />
                    {appeal.leadership_name}
                  </p>
                  <p className="appeal-preview">{appeal.message.substring(0, 100)}...</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Yangi murojaat modal */}
      {showModal && selectedLeader && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Murojaat yuborish</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="appeal-recipient">
                  <div className="recipient-avatar">
                    {selectedLeader.photo ? (
                      <img src={getPhotoUrl(selectedLeader.photo)} alt={selectedLeader.full_name} />
                    ) : (
                      <User size={32} />
                    )}
                  </div>
                  <div>
                    <strong>{selectedLeader.full_name}</strong>
                    <span>{selectedLeader.position}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Mavzu *</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                    placeholder="Murojaat mavzusi"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Xabar matni *</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Murojaatingizni batafsil yozing..."
                    rows={6}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? <Loader2 className="spinner" size={18} /> : <Send size={18} />}
                  Yuborish
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Murojaat detail modal */}
      {selectedAppeal && (
        <div className="modal-overlay" onClick={() => setSelectedAppeal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedAppeal.subject}</h2>
              <button className="modal-close" onClick={() => setSelectedAppeal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="appeal-detail-status">
                {(() => {
                  const config = STATUS_CONFIG[selectedAppeal.status];
                  const Icon = config.icon;
                  return (
                    <span style={{ background: config.bg, color: config.color }}>
                      <Icon size={16} />
                      {config.label}
                    </span>
                  );
                })()}
              </div>
              
              <div className="appeal-detail-info">
                <div className="info-item">
                  <label>Kimga:</label>
                  <strong>{selectedAppeal.leadership_name} - {selectedAppeal.leadership_position}</strong>
                </div>
                <div className="info-item">
                  <label>Sana:</label>
                  <strong>{new Date(selectedAppeal.created_at).toLocaleString('uz-UZ')}</strong>
                </div>
              </div>
              
              <div className="appeal-detail-message">
                <label>Xabar:</label>
                <p>{selectedAppeal.message}</p>
              </div>
              
              {selectedAppeal.admin_comment && (
                <div className="appeal-detail-response">
                  <label>Javob:</label>
                  <p>{selectedAppeal.admin_comment}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedAppeal(null)}>
                Yopish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentLeadership;
